import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import useLocalStorage from "utils/useLocalStorage";

export const AppContext = createContext();

// Static config — defined outside component to avoid rebuilding every render
const clientMapping = {
  "instalily.ai": [
    {
      key: "heritagepool",
      label: "HeritagePool+",
      imageUrl: "/logos/heritagepool+-logo.png",
      schema: "heritagepool_schema",
    },
    {
      key: "heritage",
      label: "Heritage+",
      imageUrl: "/logos/heritage+-logo.png",
      schema: "heritage_schema",
    },
  ],
  "srsdistribution.com": [
    {
      key: "heritagepool",
      label: "HeritagePool+",
      imageUrl: "/logos/heritagepool+-logo.png",
      schema: "heritagepool_schema",
    },
    {
      key: "heritage",
      label: "Heritage+",
      imageUrl: "/logos/heritage+-logo.png",
      schema: "heritage_schema",
    },
  ],
  "mmhfgb.com": [
    {
      key: "heritagepool",
      label: "HeritagePool+",
      imageUrl: "/logos/heritagepool+-logo.png",
      schema: "heritagepool_schema",
    },
    {
      key: "heritage",
      label: "Heritage+",
      imageUrl: "/logos/heritage+-logo.png",
      schema: "heritage_schema",
    },
  ],
};

const additionalEmailAccess = {
  "srsdistribution.com": [
    "aaron.spitz@heritagepsg.com",
    "anurag.jalan@srsdistribution.com",
    "mark.warshaw@heritagepsg.com",
    "mary.pottkotter@heritagepsg.com",
    "rin.barry@heritagepsg.com",
    "chris.cataldo@heritagelsg.com",
    "steve.okelberry@heritagelsg.com",
    "mp203598@mmhfgb.com",
    "aj097979@mmhfgb.com",
    "kent.jordan@heritagelsg.com",
    "brady.hammons@heritagelsg.com",
    "brandi.kincaid@heritagelsg.com",
    "j.t.benton@heritagepsg.com",
    "raymond.ambis@heritagepsg.com",
    "lauren.siscon@heritagepsg.com",
    "efuller1@ur.com",
    "kjackson6@ur.com",
    "pmaddison@ur.com",
    "hello@herainetwork.com",
    "ic106475@mmhfgb.com",
    "ls113278@mmhfgb.com",
    "iris.cheng@srsdistribution.com",
    "patrick.thomas@heritagepsg.com",
    "Laura.Schroeder@srsdistribution.com",
  ],
  "instalily.ai": [
    "cristinconnerney@gmail.com",
    "iris@instalily.ai",
    "iris.cheng@instalily.ai",
    "evan@instalily.ai",
    "anirudh@instalily.ai",
    "morgan@instalily.ai",
    "vyom@instalily.ai",
  ],
};

export const AppProvider = ({ children }) => {
  const { user } = useAuth0();
  const [emailDomain, setEmailDomain] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [selectedClient, setSelectedClient] = useLocalStorage(
    "selectedClient",
    null,
  );

  useEffect(() => {
    if (user) {
      const domain = user.email.split("@")[1];

      if (clientMapping[domain]) {
        // Domain-based access
        setEmailDomain(domain);
        setClientOptions(clientMapping[domain]);
      } else {
        // Email-specific access
        let assignedDomain = null;

        for (const [allowedDomain, emails] of Object.entries(
          additionalEmailAccess,
        )) {
          if (emails.includes(user.email)) {
            assignedDomain = allowedDomain;
            break;
          }
        }

        if (assignedDomain) {
          setEmailDomain(assignedDomain);
          setClientOptions(clientMapping[assignedDomain]);
        } else {
          // No access
          setEmailDomain(null);
          setClientOptions([]);
          setSelectedClient(null);
        }
      }
    }
  }, [user]);

  useEffect(() => {
    if (!selectedClient && clientOptions.length > 0) {
      setSelectedClient(clientOptions[0]);
    }
  }, [clientOptions, selectedClient]);

  // Environment-based backend URL
  const base_url =
    window.location.hostname === "localhost"
      ? "http://localhost:8080"
      : window.location.hostname.includes("stage")
        ? "https://monitoring-backend-stage-ve7hwyltsq-uc.a.run.app"
        : "https://monitoring-backend-ve7hwyltsq-uc.a.run.app";

  const { getAccessTokenSilently } = useAuth0();

  // Global Application State

  // UTC midnight `daysAgo` days before the current UTC day.
  //
  // Every getter here MUST be a UTC one. The previous version read local
  // calendar parts (`getFullYear`/`getMonth`/`getDate`) and fed them to
  // `Date.UTC`, which silently means "today" is the local date even though the
  // data — and the query that filters it — are UTC. West of Greenwich those
  // disagree for the last hours of every day: at 20:26 EDT it is already
  // 00:26 UTC tomorrow, so `end_date` was sent as *yesterday* in UTC terms and
  // the backend's `time_stamp < (end::date + 1 day)` cut off everything since
  // UTC midnight. Measured 2026-08-12: three mobile turns at 00:26 UTC were
  // invisible on every tab, with no error and no empty-state — the console
  // simply looked up to date and was not.
  //
  // Matches `utcMidnight` in components/layout/DateRangePresets.jsx; the two
  // must stay in step or the presets and the default disagree about "today".
  const utcMidnight = (daysAgo) => {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - daysAgo);
    return new Date(
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0),
    );
  };

  // Both ends are UTC midnight, so this is 14 whole days and matches the
  // "2 weeks" preset exactly. The end used to be 23:59:59.999, which made
  // DateRangePresets' inclusive `dayCount` round to 15 — so the default range
  // highlighted no preset and carried a "not a whole number of weeks" warning
  // that Overview's delta grading could never clear.
  const [dateRange, setDateRange] = useState([utcMidnight(13), utcMidnight(0)]);

  const [selectedUser, setSelectedUser] = useLocalStorage("selectedUser", null);
  const [selectedSession, setSelectedSession] = useLocalStorage(
    "selectedSession",
    null,
  );
  // Default is Overview, not Users. The legacy table tabs are hidden (see
  // HIDDEN_TABS in monitoring/Index.js) and this value is persisted in
  // localStorage -- anyone who last used the console would otherwise reopen it
  // on a tab with no button to leave from.
  const [activeTab, setActiveTab] = useLocalStorage("activeTab", "Overview");
  const [accountTypeFilter, setAccountTypeFilter] = useState(null); // null = show all, 'Internal' or 'External'

  // Global platform filter: null = all, 'web' or 'mobile'. Lives here rather
  // than per-tab so switching tabs keeps the question you were asking. Derived
  // server-side from messages.device -- never defaulted to 'desktop', which is
  // the pre-existing bug in data_retrieval.
  const [platformFilter, setPlatformFilter] = useState(null);

  // Environment view, honored by every /send endpoint. 'prod' excludes rows
  // labeled stage/local and KEEPS unlabeled rows (the tabs disclose the count);
  // 'stage' shows labeled staging only; 'all' disables the filter. Defaults to
  // production because that is the question the console exists to answer --
  // measured 2026-08-26, ~19% of a 14-day pool window was staging traffic
  // silently counted as usage on every tab.
  const [environmentFilter, setEnvironmentFilter] = useState('prod');

  // Language view. Measured 2026-08-26: chat_language is exactly 'en'/'es'
  // plus ~0.4% unlabeled rows, which appear only in the all-languages default.
  // null = all. Only the tabs whose EVERY figure honors this show the buttons
  // (Overview, Quality, Sessions) -- no inert controls.
  const [languageFilter, setLanguageFilter] = useState(null);

  // One-shot prefill for the Sessions tab, consumed on arrival. Lets any
  // number elsewhere click through to the conversations behind it without
  // Sessions growing a URL contract.
  const [sessionsPrefill, setSessionsPrefill] = useState(null);

  // Console access is granted by email domain (clientMapping above), and the
  // client's own people are on that list -- srsdistribution.com, mmhfgb.com and
  // named individuals at heritagepsg.com / heritagelsg.com. There is no role
  // system, so this is the only signal available for "is the person reading
  // this on our side".
  //
  // Used to hide engineering diagnosis, not to hide problems: every gap is still
  // listed for everyone, with what is needed to close it. What internal viewers
  // additionally see is the cause -- which table, which broken column, which
  // service went quiet -- because that reads as a defect report rather than a
  // roadmap when you have no context for it.
  const isInternal = emailDomain === "instalily.ai";

  // Raw Data State
  const [messagesData, setMessagesData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [sessionData, setSessionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const fetchIdRef = useRef(0);

  // Build request payload from current state
  const buildRequestContent = useCallback(() => {
    return {
      user_id: selectedUser,
      session_id: selectedSession,
      start_date: (() => {
        const date = new Date(dateRange[0]);
        return new Date(
          Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            0,
            0,
            0,
            0,
          ),
        );
      })(),
      end_date: (() => {
        const date = new Date(dateRange[1]);
        return new Date(
          Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            date.getUTCDate(),
            23,
            59,
            59,
            999,
          ),
        );
      })(),
      client: selectedClient?.key,
    };
  }, [selectedUser, selectedSession, dateRange, selectedClient]);

  // Single fetch effect — uses fetchId to discard stale responses
  useEffect(() => {
    if (!selectedClient || clientOptions.length === 0) return;

    const currentFetchId = ++fetchIdRef.current;

    console.log(`🔄 Data fetch #${currentFetchId} triggered:`, {
      client: selectedClient?.key,
      dateRange: dateRange.map((d) => d.toISOString()),
      selectedUser,
    });

    const fetchData = async () => {
      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const content = buildRequestContent();
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };

        const [messagesRes, usersRes] = await Promise.all([
          axios.post(`${base_url}/send/get-messages`, content, { headers }),
          axios.post(`${base_url}/send/get-users`, content, { headers }),
        ]);

        // Only update state if this is still the latest fetch
        if (currentFetchId !== fetchIdRef.current) {
          console.log(
            `⏭️ Discarding stale fetch #${currentFetchId} (current: #${fetchIdRef.current})`,
          );
          return;
        }

        setMessagesData(messagesRes.data.data || []);
        setUserData(usersRes.data.data || []);
        console.log(
          `✅ Data fetch #${currentFetchId} completed:`,
          messagesRes.data.data?.length,
          "messages,",
          usersRes.data.data?.length,
          "users",
        );
      } catch (error) {
        if (currentFetchId !== fetchIdRef.current) return;
        console.error("❌ Data fetch failed:", error);
        setMessagesData([]);
        setUserData([]);
      } finally {
        if (currentFetchId === fetchIdRef.current) {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [
    selectedClient?.key,
    dateRange,
    selectedUser,
    clientOptions.length,
    getAccessTokenSilently,
    buildRequestContent,
    base_url,
  ]);

  // Navigation helper
  const onRowClick = async (id) => {
    setSelectedUser(id);
    setActiveTab("Messages");
  };

  // Utility function to refresh data (manual trigger)
  const refreshData = useCallback(async () => {
    if (!selectedClient || clientOptions.length === 0) return;
    const currentFetchId = ++fetchIdRef.current;
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const content = buildRequestContent();
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const [messagesRes, usersRes] = await Promise.all([
        axios.post(`${base_url}/send/get-messages`, content, { headers }),
        axios.post(`${base_url}/send/get-users`, content, { headers }),
      ]);

      if (currentFetchId === fetchIdRef.current) {
        setMessagesData(messagesRes.data.data || []);
        setUserData(usersRes.data.data || []);
      }
    } catch (error) {
      if (currentFetchId === fetchIdRef.current) {
        console.error("Error refreshing data:", error);
      }
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    selectedClient,
    clientOptions,
    getAccessTokenSilently,
    buildRequestContent,
    base_url,
  ]);

  return (
    <AppContext.Provider
      value={{
        // Client and User Access
        clientOptions,
        selectedClient,
        setSelectedClient,
        emailDomain,

        // Global State
        dateRange,
        setDateRange,
        platformFilter,
        setPlatformFilter,
        environmentFilter,
        setEnvironmentFilter,
        languageFilter,
        setLanguageFilter,
        sessionsPrefill,
        setSessionsPrefill,
        isInternal,
        activeTab,
        setActiveTab,
        selectedUser,
        setSelectedUser,
        accountTypeFilter,
        setAccountTypeFilter,

        // Raw Data
        messagesData,
        userData,
        sessionData,
        loading,

        // Utilities
        onRowClick,
        refreshData,
        getAccessTokenSilently,
        base_url,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
